import { FormEvent, useEffect, useRef, useState } from "react";
import { describeCoreError } from "../../core-api/errors";
import {
  createWorkspace,
  disableWorkspace,
  loadManagedWorkspaces,
  ManagedWorkspace,
  SaveWorkspaceInput,
  updateWorkspace,
  WorkspaceAdministrationPage,
  WorkspaceStatus,
  WorkspaceType,
} from "./api";
import "./workspaces.css";

const typeNames: Record<WorkspaceType, string> = {
  CHEMICAL_REAGENT: "化学试剂业务",
  EMBODIED_INTELLIGENCE: "具身智能业务",
  GENERAL: "通用业务",
};
const statusNames: Record<WorkspaceStatus, string> = {
  DRAFT: "草稿",
  CONFIGURING: "配置中",
  PILOT: "试运行",
  ACTIVE: "已启用",
  SUSPENDED: "已禁用",
  ARCHIVED: "已归档",
};
interface Editor {
  workspace?: ManagedWorkspace;
  code: string;
  name: string;
  workspaceType: WorkspaceType;
  defaultLanguage: string;
  defaultTimezone: string;
  defaultCurrency: string;
  reason: string;
}

export function WorkspaceManagement({
  currentWorkspaceId,
  onDirectoryChanged,
}: {
  currentWorkspaceId?: string;
  onDirectoryChanged: (currentWorkspaceDisabled?: boolean) => Promise<void>;
}) {
  const [page, setPage] = useState<WorkspaceAdministrationPage>();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [workspaceType, setWorkspaceType] = useState("");
  const [queryVersion, setQueryVersion] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editor, setEditor] = useState<Editor>();
  const [disableTarget, setDisableTarget] = useState<ManagedWorkspace>();
  const [disableReason, setDisableReason] = useState("");
  const editorDialog = useRef<HTMLDialogElement>(null);
  const disableDialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    loadManagedWorkspaces(
      {
        search: search.trim() || undefined,
        status: status || undefined,
        workspaceType: workspaceType || undefined,
        page: pageNumber,
      },
      controller.signal,
    )
      .then((result) => {
        if (!controller.signal.aborted) setPage(result);
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(describeCoreError(cause));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [queryVersion, pageNumber]);

  useEffect(() => {
    if (editor) editorDialog.current?.showModal();
    else editorDialog.current?.close();
  }, [editor]);
  useEffect(() => {
    if (disableTarget) disableDialog.current?.showModal();
    else disableDialog.current?.close();
  }, [disableTarget]);

  function applyFilters(event?: FormEvent) {
    event?.preventDefault();
    setPageNumber(1);
    setQueryVersion((value) => value + 1);
  }
  function openEditor(workspace?: ManagedWorkspace) {
    setError("");
    setEditor({
      workspace,
      code: workspace?.code ?? "",
      name: workspace?.name ?? "",
      workspaceType: workspace?.workspaceType ?? "CHEMICAL_REAGENT",
      defaultLanguage: workspace?.defaultLanguage ?? "zh-CN",
      defaultTimezone: workspace?.defaultTimezone ?? "Asia/Shanghai",
      defaultCurrency: workspace?.defaultCurrency ?? "CNY",
      reason: workspace ? "空间管理：修改空间资料" : "空间管理：新增空间",
    });
  }
  function patchEditor(values: Partial<Editor>) {
    setEditor((current) => (current ? { ...current, ...values } : current));
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!editor || busy) return;
    setBusy(true);
    setError("");
    const input: SaveWorkspaceInput = {
      ...(editor.workspace ? {} : { code: editor.code.trim() }),
      name: editor.name.trim(),
      workspaceType: editor.workspaceType,
      defaultLanguage: editor.defaultLanguage.trim(),
      defaultTimezone: editor.defaultTimezone.trim(),
      defaultCurrency: editor.defaultCurrency.trim().toUpperCase(),
      reason: editor.reason.trim(),
    };
    try {
      if (editor.workspace) await updateWorkspace(editor.workspace.id, input);
      else await createWorkspace(input);
      setNotice(editor.workspace ? "空间资料已更新" : "空间已创建");
      setEditor(undefined);
      await onDirectoryChanged();
      setQueryVersion((value) => value + 1);
    } catch (cause) {
      setError(describeCoreError(cause));
    } finally {
      setBusy(false);
    }
  }
  async function confirmDisable(event: FormEvent) {
    event.preventDefault();
    if (!disableTarget || busy || !disableReason.trim()) return;
    setBusy(true);
    setError("");
    try {
      await disableWorkspace(disableTarget.id, disableReason.trim());
      const disabledCurrent = disableTarget.id === currentWorkspaceId;
      setNotice(`空间“${disableTarget.name}”已禁用`);
      setDisableTarget(undefined);
      setDisableReason("");
      await onDirectoryChanged(disabledCurrent);
      if (!disabledCurrent) setQueryVersion((value) => value + 1);
    } catch (cause) {
      setError(describeCoreError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="workspace-management" aria-label="空间管理">
      <header className="workspace-management-header">
        <div>
          <h1>空间管理</h1>
          <p>查看和维护业务空间。禁用后，该空间将无法继续切换进入。</p>
        </div>
        {page?.permissions.create && (
          <button className="primary" onClick={() => openEditor()}>
            新增空间
          </button>
        )}
      </header>
      <div className="workspace-management-content">
        <form className="workspace-filters" onSubmit={applyFilters}>
          <label>
            搜索
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="空间名称或编码"
            />
          </label>
          <label>
            状态
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">全部状态</option>
              {Object.entries(statusNames).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            业务类型
            <select
              value={workspaceType}
              onChange={(event) => setWorkspaceType(event.target.value)}
            >
              <option value="">全部类型</option>
              {Object.entries(typeNames).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button type="submit">查询</button>
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setStatus("");
              setWorkspaceType("");
              setPageNumber(1);
              setQueryVersion((value) => value + 1);
            }}
          >
            重置
          </button>
        </form>
        {error && <p role="alert">{error}</p>}
        {notice && <p role="status">{notice}</p>}
        <p className="workspace-muted">
          {loading ? "加载中…" : `共 ${page?.total ?? 0} 个空间`}
        </p>
        <div className="workspace-table-scroll">
          <table>
            <thead>
              <tr>
                <th>空间</th>
                <th>类型</th>
                <th>状态</th>
                <th>默认设置</th>
                <th>使用情况</th>
                <th>更新时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {page?.items.map((workspace) => (
                <tr key={workspace.id}>
                  <td>
                    <strong>{workspace.name}</strong>
                    <small>{workspace.code}</small>
                  </td>
                  <td>{typeNames[workspace.workspaceType]}</td>
                  <td>
                    <span
                      className={`workspace-status ${workspace.status.toLowerCase()}`}
                    >
                      {statusNames[workspace.status]}
                    </span>
                  </td>
                  <td>
                    <small>
                      {workspace.defaultLanguage ?? "未设置"} ·{` `}
                      {workspace.defaultTimezone ?? "未设置"} ·{` `}
                      {workspace.defaultCurrency ?? "未设置"}
                    </small>
                  </td>
                  <td>
                    <small>
                      {workspace.counts.memberships} 用户 ·{" "}
                      {workspace.counts.teams} 团队 ·{` `}
                      {workspace.counts.mailboxes} 邮箱
                    </small>
                  </td>
                  <td>
                    {new Date(workspace.updatedAt).toLocaleString("zh-CN")}
                  </td>
                  <td>
                    <div className="workspace-actions">
                      {page.permissions.manage &&
                        workspace.status !== "ARCHIVED" && (
                          <button onClick={() => openEditor(workspace)}>
                            编辑
                          </button>
                        )}
                      {page.permissions.disable &&
                        !["SUSPENDED", "ARCHIVED"].includes(
                          workspace.status,
                        ) && (
                          <button
                            className="danger"
                            disabled={
                              workspace.id === currentWorkspaceId &&
                              !page.isPlatformAdministrator
                            }
                            title={
                              workspace.id === currentWorkspaceId &&
                              !page.isPlatformAdministrator
                                ? "当前空间只能由超级管理员禁用"
                                : undefined
                            }
                            onClick={() => {
                              setDisableReason("");
                              setDisableTarget(workspace);
                            }}
                          >
                            禁用
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && !page?.items.length && (
                <tr>
                  <td className="workspace-empty" colSpan={7}>
                    当前筛选条件下没有空间
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <nav className="workspace-pagination" aria-label="空间分页">
          <button
            disabled={loading || pageNumber <= 1}
            onClick={() => setPageNumber((value) => Math.max(1, value - 1))}
          >
            上一页
          </button>
          <span>第 {pageNumber} 页</span>
          <button
            disabled={loading || !page?.hasNext}
            onClick={() => setPageNumber((value) => value + 1)}
          >
            下一页
          </button>
        </nav>
      </div>

      <dialog className="workspace-modal" ref={editorDialog}>
        {editor && (
          <form onSubmit={save}>
            <header>
              <h2>{editor.workspace ? "编辑空间" : "新增空间"}</h2>
              <button
                type="button"
                disabled={busy}
                onClick={() => setEditor(undefined)}
              >
                ×
              </button>
            </header>
            <div className="workspace-editor-grid">
              <label>
                空间编码
                <input
                  required
                  disabled={Boolean(editor.workspace)}
                  value={editor.code}
                  onChange={(event) =>
                    patchEditor({ code: event.target.value })
                  }
                  placeholder="例如 NEW_SPACE"
                />
              </label>
              <label>
                空间名称
                <input
                  required
                  value={editor.name}
                  onChange={(event) =>
                    patchEditor({ name: event.target.value })
                  }
                />
              </label>
              <label>
                业务类型
                <select
                  value={editor.workspaceType}
                  onChange={(event) =>
                    patchEditor({
                      workspaceType: event.target.value as WorkspaceType,
                    })
                  }
                >
                  {Object.entries(typeNames).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                默认语言
                <input
                  required
                  value={editor.defaultLanguage}
                  onChange={(event) =>
                    patchEditor({ defaultLanguage: event.target.value })
                  }
                />
              </label>
              <label>
                默认时区
                <input
                  required
                  value={editor.defaultTimezone}
                  onChange={(event) =>
                    patchEditor({ defaultTimezone: event.target.value })
                  }
                />
              </label>
              <label>
                默认币种
                <input
                  required
                  maxLength={3}
                  value={editor.defaultCurrency}
                  onChange={(event) =>
                    patchEditor({ defaultCurrency: event.target.value })
                  }
                />
              </label>
            </div>
            <label>
              操作原因
              <textarea
                required
                value={editor.reason}
                onChange={(event) =>
                  patchEditor({ reason: event.target.value })
                }
              />
            </label>
            <footer>
              <button
                type="button"
                disabled={busy}
                onClick={() => setEditor(undefined)}
              >
                取消
              </button>
              <button className="primary" disabled={busy} type="submit">
                {busy ? "保存中…" : "保存"}
              </button>
            </footer>
          </form>
        )}
      </dialog>

      <dialog
        className="workspace-modal workspace-disable-modal"
        ref={disableDialog}
      >
        {disableTarget && (
          <form onSubmit={confirmDisable}>
            <header>
              <h2>禁用空间</h2>
              <button
                type="button"
                disabled={busy}
                onClick={() => setDisableTarget(undefined)}
              >
                ×
              </button>
            </header>
            <p>
              确认禁用“{disableTarget.name}
              ”？禁用后，该空间不会继续出现在可切换空间中。
            </p>
            <label>
              操作原因
              <textarea
                required
                value={disableReason}
                onChange={(event) => setDisableReason(event.target.value)}
                placeholder="请输入禁用原因"
              />
            </label>
            <footer>
              <button
                type="button"
                disabled={busy}
                onClick={() => setDisableTarget(undefined)}
              >
                取消
              </button>
              <button
                className="danger-solid"
                disabled={busy || !disableReason.trim()}
              >
                {busy ? "处理中…" : "确认禁用"}
              </button>
            </footer>
          </form>
        )}
      </dialog>
    </section>
  );
}
